import { FC, useEffect, useState, useCallback } from "react";
import { Modal } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import useTrayPrinter from "../../../hooks/useTrayPrinter";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  loadPrintableByInvoice,
  buildTicketPrintRequest,
} from "../../../util/ticket-print";
import { useTranslation } from "../../../util/translation-provider";

type ReprintType = "invoice" | "pintag" | "bagtag" | "manifest";

interface IReprintModalProps {
  open: boolean;
  onClose: () => void;
  selectedTab?: ReprintType;
  invoiceNo?: string;
  pinTagNo?: string;
}

const REPRINT_LABELS: Record<ReprintType, { title: string; input: string; placeholder: string }> = {
  invoice:  { title: "Reprint Invoice",  input: "Invoice No.",   placeholder: "Enter invoice number" },
  pintag:   { title: "Reprint Tag",      input: "Pin Tag No.",   placeholder: "3-digit pin tag"      },
  bagtag:   { title: "Reprint Bag Tag",  input: "Bag No.",       placeholder: "Enter bag number"     },
  manifest: { title: "Reprint Manifest", input: "Manifest No.",  placeholder: "Enter manifest number"},
};

export const ReprintModal: FC<IReprintModalProps> = ({
  open,
  onClose,
  selectedTab = "invoice",
  invoiceNo: invoiceNoProp,
  pinTagNo: pinTagNoProp,
}) => {
  const [activeType, setActiveType] = useState<ReprintType>(
    selectedTab ?? "invoice"
  );
  const [invoiceNo, setInvoiceNo] = useState<string>("");
  const [pinTagNo, setPinTagNo] = useState<string>("");

  const dispatch = useAppDispatch();
  const translation = useTranslation();
  const { user } = useAppSelector((state) => state.auth);
  const { printTicket, isPrinting, lastError, status } = useTrayPrinter();
  const [selectedCopy, setSelectedCopy] = useState<"customer" | "store" | null>(
    null
  );

  useEffect(() => {
    setActiveType(selectedTab);
    setInvoiceNo(invoiceNoProp ?? "");
    setPinTagNo(pinTagNoProp ?? "");
    setSelectedCopy(null);
  }, [open, selectedTab, invoiceNoProp, pinTagNoProp]);

  const isValidInvoiceNo = (value: string): boolean => {
    return typeof value === "string" && value.trim().length > 0;
  };

  const isValidPinTagNo = (value: string): boolean => {
    const trimmed = value.trim();
    return trimmed.length == 3 && /^\d+$/.test(trimmed);
  };

  const doPrint = useCallback(
    async (variant: "customer" | "store") => {
      if (activeType !== "invoice") return;
      if (!invoiceNo.trim()) return;

      try {
        const payload = await loadPrintableByInvoice(
          dispatch,
          translation,
          invoiceNo.trim()
        );
        if (!payload) {
          console.warn("Failed to load bill data for printing.");
          return;
        }

        const printData = buildTicketPrintRequest(
          payload,
          variant === "customer" ? "receipt" : "storeCopy",
          { name: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(), id: user?.id }
        );

        const ok = await printTicket(printData);
        if (!ok) console.warn(lastError?.message ?? "Failed to print.");
      } catch (e) {
        console.error(e);
      }
    },
    [activeType, invoiceNo, printTicket, lastError, dispatch, translation, user]
  );

  const isInputValid =
    activeType === "invoice"
      ? isValidInvoiceNo(invoiceNo)
      : isValidPinTagNo(pinTagNo);

  const printingDisabled =
    isPrinting || !isInputValid;

  const hasInputError =
    activeType === "invoice"
      ? invoiceNo.trim().length > 0 && !isValidInvoiceNo(invoiceNo)
      : pinTagNo.trim().length > 0 && !isValidPinTagNo(pinTagNo);

  const currentValue = activeType === "invoice" ? invoiceNo : pinTagNo;
  const copyLabel = (copy: "customer" | "store") =>
    activeType === "invoice"
      ? copy === "customer"
        ? "Customer Copy"
        : "Store Copy"
      : copy === "customer"
      ? "Single Tag"
      : "All Tags";

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock
      disableRestoreFocus
      sx={{ zIndex: 999 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.5)" } },
      }}
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[430px] rounded-2xl bg-white shadow-2xl border border-white/80 overflow-hidden font-sans focus:outline-none">
        {/* ── Header ── */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-[#018752]/10 flex items-center justify-center shrink-0">
              <PrintIcon sx={{ fontSize: "1.25rem", color: "#018752" }} />
            </div>
            <div>
              <h2 className="text-[18px] font-bold text-[#515151] leading-tight">
                {REPRINT_LABELS[activeType].title}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Enter {REPRINT_LABELS[activeType].input.toLowerCase()} and select a print option.
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-[#515151] transition shrink-0"
          >
            <CloseIcon sx={{ fontSize: "1.0625rem" }} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="px-6 py-5 space-y-5">
          {/* Input card */}
          <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4 shadow-inner">
            <label
              htmlFor="reprint-number"
              className="text-xs font-semibold text-[#515151] flex items-center gap-2 mb-2"
            >
              {activeType === "pintag" || activeType === "bagtag" ? (
                <LocalOfferIcon sx={{ fontSize: "0.9375rem", color: "#0079C2" }} />
              ) : (
                <ReceiptLongIcon sx={{ fontSize: "0.9375rem", color: "#0079C2" }} />
              )}
              {REPRINT_LABELS[activeType].input}
            </label>
            <input
              id="reprint-number"
              autoComplete="off"
              value={currentValue}
              placeholder={REPRINT_LABELS[activeType].placeholder}
              onChange={(e) => {
                if (activeType === "invoice") setInvoiceNo(e.target.value);
                else setPinTagNo(e.target.value);
              }}
              className={`w-full h-11 rounded-xl border bg-white px-4 text-sm font-semibold text-[#515151] outline-none transition focus:ring-4 ${
                hasInputError
                  ? "border-[#930000] focus:border-[#930000] focus:ring-[#930000]/10"
                  : "border-gray-200 focus:border-[#018752] focus:ring-[#018752]/10"
              }`}
            />
            {hasInputError && (
              <p className="mt-2 text-xs font-medium text-[#930000]">
                {activeType === "invoice"
                  ? "Invoice number is required."
                  : "Pin tag must be exactly 3 digits."}
              </p>
            )}
          </div>

          {/* Print option */}
          <div>
            <p className="text-xs font-semibold text-[#515151] mb-2">Print Option</p>
            <div className="grid grid-cols-2 gap-3">
              {(["customer", "store"] as const).map((copy) => (
                <button
                  key={copy}
                  type="button"
                  disabled={printingDisabled}
                  onClick={() => setSelectedCopy(copy)}
                  aria-pressed={selectedCopy === copy}
                  className={`h-11 rounded-xl border px-5 text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${
                    selectedCopy === copy
                      ? "border-[#018752] bg-[#018752]/10 text-[#018752] shadow-sm"
                      : "border-gray-200 bg-gray-50 text-[#515151] hover:bg-gray-100"
                  }`}
                >
                  {copyLabel(copy)}
                </button>
              ))}
            </div>
          </div>

          {/* Summary box */}
          {selectedCopy && isInputValid && (
            <div className="rounded-xl bg-[#018752]/5 border border-[#018752]/15 px-4 py-3 flex items-center gap-3">
              <CheckCircleOutlineIcon
                sx={{ fontSize: "1.125rem", color: "#018752", flexShrink: 0 }}
              />
              <p className="text-xs text-[#515151] leading-relaxed">
                Ready to reprint{" "}
                <span className="font-bold">{copyLabel(selectedCopy)}</span> for{" "}
                {REPRINT_LABELS[activeType].input.toLowerCase()}{" "}
                <span className="font-bold">{currentValue || "—"}</span>.
              </p>
            </div>
          )}

          {/* Printer status */}
          {status === "error" && (
            <p className="text-xs text-center text-gray-500">
              {lastError?.message ?? "Printer error"}
            </p>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-6 pb-6">
          <button
            type="button"
            disabled={isPrinting || !isInputValid || !selectedCopy}
            onClick={async () => {
              if (selectedCopy) await doPrint(selectedCopy);
              onClose();
            }}
            className={`w-full h-12 rounded-xl text-sm font-bold transition active:scale-[0.99] ${
              !isPrinting && isInputValid && selectedCopy
                ? "bg-[#018752] text-white shadow-lg shadow-[#018752]/20 hover:brightness-95"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isPrinting ? "Printing…" : "Done"}
          </button>
        </div>
      </div>
    </Modal>
  );
};
